import React, { FC, useState } from 'react';
import { getSearchSuggestions, getSearchPlace } from '@/api/search';
import { SearchBarSuggestion } from
  '@/pages/MarketReportsListingPage/components/SearchBar/SearchBarSuggestions/SearchBarSuggestions';
import SearchBarInput from '@/pages/MarketReportsListingPage/components/SearchBar/SearchBarInput/SearchBarInput';
import styles from './SearchBar.module.scss';

interface SearchBarProps {
  invalidMessage: string;
  placeholder?: string;
  currentSearch?: string;
}

interface SuggestionsResponse {
  suggestions: SearchBarSuggestion[];
}

interface PlaceInfoResponse {
  place: string;
  cityName: string;
  countryCode: string;
  countryName: string;
  address: string;
  placeId: string;
}

const encodeSpaces = (value: string): string => value.replace(/\s/g, '%2D');

const SearchBar: FC<SearchBarProps> = ({
  placeholder = '', invalidMessage, currentSearch,
}: SearchBarProps) => {
  const [suggestions, setSuggestions] = useState<SearchBarSuggestion[] | null>(null);
  const [isValid, setIsValid] = useState(true);

  const invalidate = (): void => {
    setIsValid(false);
  };

  const updateSuggestions = ({ suggestions: newSuggestions }: SuggestionsResponse): void => {
    if (!newSuggestions) {
      return;
    }
    setSuggestions(newSuggestions);
    if (suggestions) {
      setIsValid(!!newSuggestions.length);
    }
  };

  const onValueChange = (newValue: string): void => {
    if (!newValue) {
      return;
    }

    getSearchSuggestions(newValue, updateSuggestions, invalidate);
  };

  const onClearClick = (): void => {
    setSuggestions([]);
    setIsValid(true);
    window.location.replace(window.location.pathname);
  };

  const redirectToSearchResults = (placeInfo: PlaceInfoResponse): void => {
    const {
      address, place, cityName, countryCode, countryName, placeId,
    } = placeInfo;

    if (!address) {
      return;
    }

    const { pathname } = window.location;
    const placeEncoded = place ? `place=${encodeSpaces(place)}&` : '';
    const cityEncoded = encodeSpaces(cityName);
    const countryEncoded = encodeSpaces(countryName);

    const searchUrl = `${pathname}?${placeEncoded}city=${cityEncoded}&countryCode=${countryCode}&countryName=${countryEncoded}&placeId=${placeId}`;
    window.location.replace(searchUrl);
  };

  const selectPlace = (placeId: string): void => {
    if (!placeId) {
      return;
    }

    getSearchPlace(placeId, redirectToSearchResults, invalidate);
  };

  return (
    <div className={styles.container}>
      <SearchBarInput
        initValue={currentSearch}
        placeholder={placeholder}
        onChange={onValueChange}
        onClear={onClearClick}
        isValid={isValid}
        suggestions={suggestions}
        onSuggestionClick={selectPlace}
        onSearch={selectPlace}
      />
      {(invalidMessage && !isValid) && <div className={styles.invalidMessage}>{invalidMessage}</div>}
    </div>
  );
};

export default SearchBar;
